import React, { useEffect, useRef, useState } from 'react'
import {
    Box, Button, Checkbox, Divider, FormControlLabel, IconButton,
    InputAdornment, MenuItem, TextField, Tooltip, Typography
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import { Colors } from '../../../../Styles/Colors'
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg'
import RestAPIService from '../../../../Utility/Services/RestAPIService'
import { Serviceurls } from '../../../../Utility/API/Serviceurls'
import { localGetItem } from '../../../../Utility/Services/CacheProviderService'
import Loader from '../../../../Components/Loader'
import { useLocation, useHistory } from 'react-router-dom'
import {
    handleCertificateRichTextPaste,
    normalizeCertificateEditorTypography,
    CERTIFICATE_EDITOR_FIXED_FONT_FAMILY,
    CERTIFICATE_EDITOR_FIXED_FONT_SIZE,
} from '../certificateEditorPasteUtils'

const PREDEFINED_FIELDS = [ 'Patient Name', 'UHID', 'Gender', 'Age', 'Mobile No' ,'Doctor Name', 'Hospital Name', 'OP Number', "Purpose of Visit" ]

export default function DocCusCertTempCreation() {
    const editorRef = useRef(null)
    const savedRangeRef = useRef(null)
    const location = useLocation()
    const history = useHistory();
    const editData = location.state?.editData

    const [isLoader, setIsLoader] = useState(false)
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [templateName, setTemplateName] = useState(editData?.template_name || '')
    const [documentType, setDocumentType] = useState(editData?.document_type || '')
    const [isActive, setIsActive] = useState(editData?.is_active ?? true)
    const [fieldSearch, setFieldSearch] = useState('')
    const [customFields, setCustomFields] = useState([])
    const [newFieldName, setNewFieldName] = useState('')
    const [editingFieldIdx, setEditingFieldIdx] = useState(null)
    const [editingFieldVal, setEditingFieldVal] = useState('')
    const [newFieldType, setNewFieldType] = useState('text')
    const [bold, setBold] = useState(false)
    const [italic, setItalic] = useState(false)
    const [underline, setUnderline] = useState(false)


    useEffect(() => {
        if (editData) {
            if (editorRef.current && editData.description) {
                editorRef.current.innerHTML = editData.description
                requestAnimationFrame(() => {
                    normalizeCertificateEditorTypography(editorRef.current)
                })
            }
            if (editData.used_keys?.length) {
                const customOnes = editData.used_keys
                    .filter(k => !PREDEFINED_FIELDS.includes(k.key_name))
                    .map(k => ({ name: k.key_name, type: k.type }))
                setCustomFields(customOnes)
            }
        }
    }, [])

    const goBack = () => {
        history.push('/DoctorSettings/CustomCertificateTemplate');
    }

    const errorMessage = (msg) => {
        seterrorMsgText(msg)
        seterrorMsg(true)
    }

    const successMessage = (msg) => {
        setsuccessMsgText(msg)
        setsuccessMsg(true)
    }

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    const editorOwnsBoundaryNode = (editor, node) => {
        if (!editor || !node) return false
        if (node === editor) return true
        return editor.contains(node)
    }

    // Save cursor range before focus leaves editor
    const saveCursorRange = () => {
        const sel = window.getSelection()
        const editor = editorRef.current
        if (sel && sel.rangeCount > 0 && editor) {
            const range = sel.getRangeAt(0)
            if (editorOwnsBoundaryNode(editor, range.commonAncestorContainer)) {
                savedRangeRef.current = range.cloneRange()
            }
        }
    }

    const restoreRange = () => {
        const editor = editorRef.current
        if (!editor) return false
        editor.focus()
        if (savedRangeRef.current) {
            // Validate that the saved range still references nodes inside the editor
            const range = savedRangeRef.current
            if (
                editorOwnsBoundaryNode(editor, range.startContainer)
                && editorOwnsBoundaryNode(editor, range.endContainer)
            ) {
                try {
                    const sel = window.getSelection()
                    sel.removeAllRanges()
                    sel.addRange(range)
                    return true
                } catch (e) {
                    // Range is invalid, discard it
                    savedRangeRef.current = null
                }
            } else {
                // Stale range — nodes were removed, discard it
                savedRangeRef.current = null
            }
        }
        return false
    }

    /** Stops the toolbar control from taking focus so the contentEditable selection stays active. */
    const keepSelectionOnToolbarPointerDown = (e) => {
        e.preventDefault()
    }

    const placeCursorAtEnd = () => {
        const editor = editorRef.current
        if (!editor) return
        editor.focus()
        const sel = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        sel.removeAllRanges()
        sel.addRange(range)
    }

    const insertFieldAtCursor = (fieldName) => {
        const restored = restoreRange()
        if (!restored) {
            placeCursorAtEnd()
        }

        // Create the field tag span
        const span = document.createElement('span')
        span.contentEditable = 'false'
        span.style.fontWeight = 'bold'
        span.style.fontStyle = 'italic'
        span.style.cursor = 'pointer'
        span.style.userSelect = 'none'
        span.textContent = `<${fieldName}>`

        // Create a trailing text node so cursor lands inline after the tag
        const space = document.createTextNode('\u00A0')

        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0)
            range.deleteContents()

            // Insert space first, then span before it — keeps everything inline
            range.insertNode(space)
            range.insertNode(span)

            // Move cursor after the space (after the inserted tag)
            const newRange = document.createRange()
            newRange.setStartAfter(space)
            newRange.collapse(true)
            sel.removeAllRanges()
            sel.addRange(newRange)
        }

        saveCursorRange()
    }

    const execCmd = (cmd, value = null) => {
        editorRef.current?.focus()
        document.execCommand(cmd, false, value)
        updateToolbarState()
        saveCursorRange()
    }

    /** True when an &lt;li&gt; has no visible text and no meaningful embedded nodes (e.g. field tags). */
    const isListItemEffectivelyEmpty = (li) => {
        const text = (li.innerText || '').replace(/\u00A0/g, ' ').trim()
        if (text.length > 0) return false
        if (li.querySelector('img, span[contenteditable="false"]')) return false
        return true
    }

    /**
     * Browsers turn each block (including blank lines) into its own list item.
     * Remove empty &lt;li&gt;s so spacing lines do not get bullets/numbers.
     */
    const cleanupEmptyListItems = (editor) => {
        if (!editor) return
        let changed = true
        while (changed) {
            changed = false
            const lists = [...editor.querySelectorAll('ul, ol')]
            for (const list of lists) {
                for (const child of [...list.children]) {
                    if (child.tagName === 'LI' && isListItemEffectivelyEmpty(child)) {
                        child.remove()
                        changed = true
                    }
                }
                if (list.parentNode && list.children.length === 0) {
                    list.remove()
                    changed = true
                }
            }
        }
    }

    const applyListCommand = (command) => {
        const editor = editorRef.current
        if (!editor) return
        restoreRange()
        editor.focus()
        document.execCommand(command, false, null)
        cleanupEmptyListItems(editor)
        updateToolbarState()
        saveCursorRange()
    }

    const updateToolbarState = () => {
        setBold(document.queryCommandState('bold'))
        setItalic(document.queryCommandState('italic'))
        setUnderline(document.queryCommandState('underline'))
    }

    const handleSave = () => {
        if (!templateName.trim()) {
            errorMessage('Please enter a Template Name.')
            return
        }
        const htmlContent = editorRef.current.innerHTML
        const innerText = editorRef.current.innerText || ''
        const keyRegex = /<([^<>]+)>/g
        const keysFound = []
        let m
        while ((m = keyRegex.exec(innerText)) !== null) {
            keysFound.push(m[1])
        }
        const uniqueKeys = [...new Set(keysFound)]
        const allFieldsWithType = [
            ...PREDEFINED_FIELDS.map(f => ({ key_name: f, type: 'text' })),
            ...customFields.map(f => ({ key_name: f.name, type: f.type }))
        ]
        const keysUsed = uniqueKeys.map(k => {
            const found = allFieldsWithType.find(f => f.key_name === k)
            return { key_name: k, type: found ? found.type : 'text' }
        })
        const data = {
            template_name: templateName.trim(),
            // document_type: documentType,
            is_active: isActive,
            description: htmlContent,
            used_keys: keysUsed,
            clinic_id : localGetItem("SelectedDoctorClinicId") || null
        }
        if(editData){
            data['id'] = editData?.id || null
        }
        setIsLoader(true)
        RestAPIService.create(data, Serviceurls.CUSTOM_CERTIFICATE_TEMPLATE)
            .then((response) => {
                setIsLoader(false)
                if (response.data.status === 'success') {
                    successMessage(response.data.message)
                    setTemplateName('')
                    setDocumentType('')
                    setIsActive(true)
                    if (editorRef.current) editorRef.current.innerHTML = ''
                    goBack()
                }
            }).catch(e => {
                setIsLoader(false)
                if (e?.response?.data?.status === 'fail') {
                    errorMessage(e.response.data.message)
                } else {
                    errorMessage(e.message)
                }
            })
    }

    const addCustomField = () => {
        if (!newFieldName.trim()) return
        const trimmed = newFieldName.trim()
        const allNames = [
            ...PREDEFINED_FIELDS.map(f => f.toLowerCase()),
            ...customFields.map(f => f.name.toLowerCase())
        ]
        if (allNames.includes(trimmed.toLowerCase())) {
            errorMessage(`"${trimmed}" already exists. Please use a different field name.`)
            return
        }
        setCustomFields(prev => [...prev, { name: trimmed, type: newFieldType }])
        // insertFieldAtCursor(trimmed)
        setNewFieldName('')
    }

    const deleteCustomField = (idx) => {
        setCustomFields(prev => prev.filter((_, i) => i !== idx))
    }

    const startEditField = (idx) => {
        setEditingFieldIdx(idx)
        setEditingFieldVal(customFields[idx].name)
    }

    const confirmEditField = (idx) => {
        const trimmed = editingFieldVal.trim()

        if (!trimmed) {
            errorMessage('Field name cannot be empty')
            return
        }

        const original = customFields[idx].name
        if (trimmed.length < original.length) {
            errorMessage('Partial field name editing is not allowed')
            return
        }

        setCustomFields(prev =>
            prev.map((f, i) =>
                i === idx ? { ...f, name: trimmed } : f
            )
        )
        setEditingFieldIdx(null)
        setEditingFieldVal('')
    }

    const applyAlignment = (align) => {
        const BLOCK_TAGS = new Set([
            'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
            'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TD', 'TH'
        ])

        const editor = editorRef.current
        if (!editor) return
        restoreRange()
        editor.focus()
        const sel = window.getSelection()

        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
            document.execCommand(
                align === 'left' ? 'justifyLeft'
                    : align === 'center' ? 'justifyCenter'
                        : align === 'right' ? 'justifyRight'
                            : 'justifyFull'
                , false, null)
            saveCursorRange()
            return
        }

        const range = sel.getRangeAt(0)

        const blocks = []
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT, {
            acceptNode: (node) => {
                if (!BLOCK_TAGS.has(node.tagName)) return NodeFilter.FILTER_SKIP
                const nodeRange = document.createRange()
                nodeRange.selectNode(node)
                const startToEnd = range.compareBoundaryPoints(Range.START_TO_END, nodeRange)
                const endToStart = range.compareBoundaryPoints(Range.END_TO_START, nodeRange)
                if (startToEnd > 0 && endToStart < 0) return NodeFilter.FILTER_ACCEPT
                return NodeFilter.FILTER_SKIP
            }
        })

        let node
        while ((node = walker.nextNode())) blocks.push(node)

        if (blocks.length === 0) {
            let container = range.commonAncestorContainer
            if (container.nodeType === Node.TEXT_NODE) container = container.parentElement
            while (container && container !== editor) {
                if (BLOCK_TAGS.has(container.tagName)) {
                    blocks.push(container)
                    break
                }
                container = container.parentElement
            }
            if (blocks.length === 0) blocks.push(editor)
        }

        blocks.forEach(block => {
            block.style.textAlign = align

            // Fix bullet/number marker alignment:
            // list-style-position:outside (default) pins the marker to the left
            // regardless of text-align. Switch to 'inside' so the marker travels
            // with the text, and restore 'outside' only when aligning left.
            if (block.tagName === 'UL' || block.tagName === 'OL') {
                block.style.listStylePosition = align === 'left' ? 'outside' : 'inside'
                if (align === 'right') {
                    block.style.paddingLeft = '0'
                    block.style.paddingRight = '1.5em'
                } else if (align === 'center') {
                    block.style.paddingLeft = '0'
                    block.style.paddingRight = '0'
                } else {
                    block.style.paddingLeft = ''
                    block.style.paddingRight = ''
                }
            }

            if (block.tagName === 'LI') {
                block.style.listStylePosition = align === 'left' ? 'outside' : 'inside'
            }
        })
        saveCursorRange()
    }

    const allFields = [...PREDEFINED_FIELDS, ...customFields.map(f => f.name)]
    const filteredPredefined = PREDEFINED_FIELDS.filter(f => f.toLowerCase().includes(fieldSearch.toLowerCase()))
    const filteredCustom = customFields.filter(f => f.name.toLowerCase().includes(fieldSearch.toLowerCase()))

    return (
        <Box component={'div'} sx={{ height: '79.5vh', width: '94vw', backgroundColor: Colors.Background, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ backgroundColor: 'white', padding: '0.8vw 1vw', margin: '0.5vw 0.5vw 0 0.5vw', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4vw', padding: '0.5vw 0.8vw' }}>
                    <IconButton size='small' onClick={goBack} sx={{ padding: '0.2vw' }}>
                        <ArrowBackIosNewIcon sx={{ fontSize: '1vw', color: Colors.Primary }} />
                    </IconButton>
                    <Typography
                        sx={{ fontSize: '0.85vw', color: Colors.Primary, cursor: 'pointer', fontWeight: 500 }}
                        onClick={goBack}
                    >
                        Back to Template Configuration Home
                    </Typography>
                </Box>
                {/* <Typography sx={{ fontSize: '0.9vw', fontWeight: 600, mb: '0.8vw', color: '#333' }}>Template Details</Typography> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1vw', flexWrap: 'wrap', marginLeft: "2vw" }}>
                    <TextField
                        label="Template Name"
                        size='small'
                        inputProps={{ maxLength: 50 }}
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        sx={{ width: '18vw', '& .MuiInputBase-input': { fontSize: '0.85vw' }, '& .MuiInputLabel-root': { fontSize: '0.85vw' } }}
                    />
                    {/* <TextField
                        select
                        label="Document Type"
                        size='small'
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                        sx={{ width: '18vw', '& .MuiInputBase-input': { fontSize: '0.85vw' }, '& .MuiInputLabel-root': { fontSize: '0.85vw' } }}
                    >
                        <MenuItem value=''>-- Select --</MenuItem>
                        <MenuItem value='Medical Certificate'>Medical Certificate</MenuItem>
                        <MenuItem value='Consent Form'>Consent Form</MenuItem>
                        <MenuItem value='Discharge Summary'>Discharge Summary</MenuItem>
                        <MenuItem value='Other'>Other</MenuItem>
                    </TextField> */}
                    <Button
                        variant='outlined'
                        size='small'
                        sx={{ textTransform: 'uppercase', fontSize: '0.78vw', borderColor: '#bbb', color: '#555', minWidth: '5vw' }}
                        onClick={() => {
                            setTemplateName('')
                            setDocumentType('')
                            setIsActive(true)
                            if (editorRef.current) editorRef.current.innerHTML = ''
                        }}
                    >
                        Clear
                    </Button>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                size='small'
                                sx={{ color: '#04B7B1', '&.Mui-checked': { color: '#04B7B1' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: '0.85vw' }}>Active</Typography>}
                        sx={{ ml: 0 }}
                    />
                </Box>
            </Box>

            {/* Editor + Sidebar */}
            <Box sx={{ display: 'flex', flex: 1, gap: '0.5vw', padding: '0.5vw', overflow: 'hidden' }}>
                {/* Left: Rich Text Editor */}
                <Box sx={{ flex: 1, backgroundColor: 'white', borderRadius: '4px', display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    {/* Toolbar */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.1vw',
                        borderBottom: '1px solid #e0e0e0', padding: '0.3vw 0.5vw', backgroundColor: '#fafafa'
                    }}>
                        {/* Undo / Redo */}
                        <Tooltip title="Undo" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('undo')} sx={{ padding: '0.2vw' }}>
                                <UndoIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Redo" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('redo')} sx={{ padding: '0.2vw' }}>
                                <RedoIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>

                        <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                        {/* Text Styles */}
                        <Tooltip title="Bold" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('bold')} sx={{ padding: '0.2vw', backgroundColor: bold ? '#e3f2fd' : 'transparent' }}>
                                <FormatBoldIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Italic" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('italic')} sx={{ padding: '0.2vw', backgroundColor: italic ? '#e3f2fd' : 'transparent' }}>
                                <FormatItalicIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Underline" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('underline')} sx={{ padding: '0.2vw', backgroundColor: underline ? '#e3f2fd' : 'transparent' }}>
                                <FormatUnderlinedIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Strikethrough" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('strikeThrough')} sx={{ padding: '0.2vw' }}>
                                <StrikethroughSIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>

                        <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                        {/* Lists */}
                        <Tooltip title="Bullet List" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyListCommand('insertUnorderedList')} sx={{ padding: '0.2vw' }}>
                                <FormatListBulletedIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Numbered List" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyListCommand('insertOrderedList')} sx={{ padding: '0.2vw' }}>
                                <FormatListNumberedIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Indent" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => execCmd('indent')} sx={{ padding: '0.2vw' }}>
                                <FormatIndentIncreaseIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>

                        <Divider orientation='vertical' flexItem sx={{ mx: '0.3vw' }} />

                        {/* Alignment */}
                        <Tooltip title="Align Left" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('left')} sx={{ padding: '0.2vw' }}>
                                <FormatAlignLeftIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Align Center" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('center')} sx={{ padding: '0.2vw' }}>
                                <FormatAlignCenterIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Align Right" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('right')} sx={{ padding: '0.2vw' }}>
                                <FormatAlignRightIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Justify" placement='top'>
                            <IconButton size='small' onMouseDown={keepSelectionOnToolbarPointerDown} onClick={() => applyAlignment('justify')} sx={{ padding: '0.2vw' }}>
                                <FormatAlignJustifyIcon sx={{ fontSize: '1.1vw' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Editable Content Area */}
                    <Box
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onMouseUp={() => { saveCursorRange(); updateToolbarState() }}
                        onKeyUp={() => { saveCursorRange(); updateToolbarState() }}
                        onSelect={() => {
                            saveCursorRange()
                            updateToolbarState()
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;')
                            }
                        }}
                        onPaste={(e) => {
                            handleCertificateRichTextPaste(e, editorRef.current)
                            requestAnimationFrame(() => {
                                saveCursorRange()
                                updateToolbarState()
                            })
                        }}
                        sx={{
                            flex: 1,
                            padding: '1vw 1.5vw',
                            outline: 'none',
                            overflowY: 'auto',
                            lineHeight: 1.6,
                            minHeight: '20vw',
                            fontFamily: CERTIFICATE_EDITOR_FIXED_FONT_FAMILY,
                            fontSize: CERTIFICATE_EDITOR_FIXED_FONT_SIZE,
                            '&, & *': {
                                fontFamily: `${CERTIFICATE_EDITOR_FIXED_FONT_FAMILY} !important`,
                                fontSize: `${CERTIFICATE_EDITOR_FIXED_FONT_SIZE} !important`,
                            },
                            '&:empty:before': {
                                content: '"Start typing your template here..."',
                                color: '#aaa',
                                pointerEvents: 'none'
                            },
                        }}
                    />

                    {/* Save Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '0.6vw 1vw', borderTop: '1px solid #e0e0e0' }}>
                        <Button
                            variant='contained'
                            onClick={handleSave}
                            sx={{
                                backgroundColor: '#04B7B1',
                                '&:hover': { backgroundColor: '#039e99' },
                                textTransform: 'none',
                                fontSize: '0.85vw',
                                padding: '0.4vw 2vw'
                            }}
                        >
                            Save Template
                        </Button>
                    </Box>
                </Box>

                {/* Right: Field Panel */}
                <Box sx={{ width: '18vw', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Field Type Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6vw 0.8vw', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography sx={{ fontSize: '0.85vw', fontWeight: 600 }}>Field Type</Typography>
                        {/* Text / Date toggle pill */}
                        <Box sx={{ display: 'flex', border: '1px solid #04B7B1', borderRadius: '4px', overflow: 'hidden' }}>
                            <Box
                                onClick={() => setNewFieldType('text')}
                                sx={{
                                    padding: '0.15vw 0.6vw', cursor: 'pointer', fontSize: '0.72vw', fontWeight: 600,
                                    backgroundColor: newFieldType === 'text' ? '#04B7B1' : 'transparent',
                                    color: newFieldType === 'text' ? 'white' : '#04B7B1',
                                    userSelect: 'none'
                                }}
                            >Text</Box>
                            <Box
                                onClick={() => setNewFieldType('date')}
                                sx={{
                                    padding: '0.15vw 0.6vw', cursor: 'pointer', fontSize: '0.72vw', fontWeight: 600,
                                    backgroundColor: newFieldType === 'date' ? '#04B7B1' : 'transparent',
                                    color: newFieldType === 'date' ? 'white' : '#04B7B1',
                                    borderLeft: '1px solid #04B7B1', userSelect: 'none'
                                }}
                            >Date</Box>
                        </Box>
                    </Box>

                    {/* Search */}
                    <Box sx={{ padding: '0.5vw 0.8vw', borderBottom: '1px solid #e0e0e0' }}>
                        <TextField
                            size='small'
                            placeholder='Search Fields'
                            value={fieldSearch}
                            onChange={(e) => setFieldSearch(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <SearchIcon sx={{ fontSize: '1vw', color: '#9e9e9e' }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: '100%', '& .MuiInputBase-input': { fontSize: '0.78vw', padding: '0.3vw 0.5vw' } }}
                        />
                    </Box>

                    {/* Fields List */}
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {/* Predefined Fields */}
                        {filteredPredefined.map((field) => (
                            <Box key={field} sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '0.4vw 0.8vw', borderBottom: '1px solid #f0f0f0',
                                '&:hover': { backgroundColor: '#f9f9f9' }
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.3vw' }}>
                                    <Typography sx={{ fontSize: '0.8vw', color: '#333' }}>{field}</Typography>
                                    <Box sx={{
                                        backgroundColor: '#e3f2fd', color: '#1565c0',
                                        fontSize: '0.55vw', fontWeight: 700, padding: '0.05vw 0.3vw',
                                        borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.03em'
                                    }}>text</Box>
                                </Box>
                                <Tooltip title={`Insert <${field}>`} placement='left'>
                                    <IconButton
                                        size='small'
                                        onClick={() => insertFieldAtCursor(field)}
                                        sx={{
                                            backgroundColor: '#04B7B1', color: 'white', width: '1.3vw', height: '1.3vw',
                                            '&:hover': { backgroundColor: '#039e99' }
                                        }}
                                    >
                                        <AddIcon sx={{ fontSize: '0.85vw' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}

                        {/* Custom Fields */}
                        {filteredCustom.map((field, idx) => {
                            const realIdx = customFields.findIndex(f => f.name === field.name)
                            return (
                                <Box key={`custom-${realIdx}`} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '0.4vw 0.8vw', borderBottom: '1px solid #f0f0f0',
                                    '&:hover': { backgroundColor: '#f9f9f9' }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.3vw', flex: 1 }}>
                                        <IconButton size='small' onClick={() => deleteCustomField(realIdx)} sx={{ padding: '0.1vw', color: '#d32f2f' }}>
                                            <DeleteIcon sx={{ fontSize: '0.85vw' }} />
                                        </IconButton>
                                        {editingFieldIdx === realIdx ? (
                                            <TextField
                                                size='small'
                                                value={editingFieldVal}
                                                onChange={(e) => setEditingFieldVal(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') confirmEditField(realIdx) }}
                                                autoFocus
                                                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78vw', padding: '0.2vw 0.4vw' } }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.3vw', flex: 1 }}>
                                                <Typography
                                                    sx={{ fontSize: '0.8vw', color: '#333', cursor: 'pointer' }}
                                                    onClick={() => startEditField(realIdx)}
                                                >
                                                    {field.name}
                                                </Typography>
                                                <Box sx={{
                                                    backgroundColor: field.type === 'date' ? '#fff3e0' : '#e3f2fd',
                                                    color: field.type === 'date' ? '#e65100' : '#1565c0',
                                                    fontSize: '0.55vw', fontWeight: 700, padding: '0.05vw 0.3vw',
                                                    borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.03em'
                                                }}>{field.type}</Box>
                                            </Box>
                                        )}
                                    </Box>
                                    {editingFieldIdx === realIdx ? (
                                        <Tooltip title="Confirm" placement='left'>
                                            <IconButton
                                                size='small'
                                                onClick={() => confirmEditField(realIdx)}
                                                sx={{ backgroundColor: '#04B7B1', color: 'white', width: '1.3vw', height: '1.3vw', '&:hover': { backgroundColor: '#039e99' } }}
                                            >
                                                <CheckIcon sx={{ fontSize: '0.85vw' }} />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title={`Insert <${field.name}>`} placement='left'>
                                            <IconButton
                                                size='small'
                                                onClick={() => insertFieldAtCursor(field.name)}
                                                sx={{ backgroundColor: '#04B7B1', color: 'white', width: '1.3vw', height: '1.3vw', '&:hover': { backgroundColor: '#039e99' } }}
                                            >
                                                <AddIcon sx={{ fontSize: '0.85vw' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )
                        })}

                        {/* Add New Custom Field Input */}
                        <Box sx={{ padding: '0.5vw 0.8vw', borderTop: '1px solid #e0e0e0', mt: 'auto' }}>
                            <TextField
                                size='small'
                                placeholder='Any Field Name Here'
                                value={newFieldName}
                                onChange={(e) => setNewFieldName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') addCustomField() }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <Tooltip title="Add custom field" placement='top'>
                                                <IconButton
                                                    size='small'
                                                    onClick={addCustomField}
                                                    disabled={!newFieldName.trim()}
                                                    sx={{ padding: '0.2vw', color: newFieldName.trim() ? '#04B7B1' : '#ccc' }}
                                                >
                                                    <CheckIcon sx={{ fontSize: '0.9vw' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ width: '100%', '& .MuiInputBase-input': { fontSize: '0.78vw', padding: '0.3vw 0.5vw' } }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
            {errorMsg && <ToastMsg severity={'error'} msg={errorMsgText} msgPop={msgClose} />}
            {successMsg && <ToastMsg severity={'success'} msg={successMsgText} msgPop={msgClose} />}
            <Loader loaderOpen={isLoader} />
        </Box>
    )
}
